import React, { useState } from 'react';
import { Reorder } from 'framer-motion';
import { ArrowLeftIcon } from 'lucide-react';
import type { Deliverable } from '../../types/portal';
import { usePortal } from '../../contexts/PortalContext';
import { AddDeliverable } from '../../components/portal/AddDeliverable';
import { DeliverableAdmin } from '../../components/portal/DeliverableAdmin';
import { ProjectFields } from '../../components/portal/ProjectFields';
import { projectStatus } from '../../utils/status';
import { ClientLink } from '../../components/portal/ClientLink';
import { PortalTeam } from '../../components/portal/PortalTeam';
import { ProjectOptions } from '../../components/portal/ProjectOptions';
import { BUTTON_QUIET, BUTTON_SOLID, FIELD } from '../../components/portal/adminStyles';
export function ProjectAdmin() {
  const {
    activeProject,
    goPortal,
    // duplicateProject stays available in context for a future project
    // options menu — it is simply not a prominent action here.
    saveAsTemplate,
    archiveProject,
    reopenProject,
    setDeliverableOrder,
    notify
  } = usePortal();
  /* One panel open at a time — matching ProjectOptions.tsx below, rather than
     Edit / Save-as-Template / Archive-confirm being three independent
     toggles that could all be open together. */
  type SettingsPanel = 'none' | 'editing' | 'template' | 'archive';
  const [panel, setPanel] = useState<SettingsPanel>('none');
  const editing = panel === 'editing';
  const savingTemplate = panel === 'template';
  const confirmArchive = panel === 'archive';
  const [templateName, setTemplateName] = useState('');
  const [carryingId, setCarryingId] = useState<string | null>(null);
  const project = activeProject;
  const total = project.deliverables.length;
  const hidden = project.deliverables.filter(item => !item.visibleToClient).length;
  function saveTemplate(event: React.FormEvent) {
    event.preventDefault();
    const name = templateName.trim();
    if (!name) return;
    saveAsTemplate(name);
    setTemplateName('');
    setPanel('none');
    notify(`Template saved · ${name}`);
  }
  return <div data-id="element-763" data-mp="27da6eoyo5pv1">
      <button type="button" onClick={() => goPortal('projects')} className="label group flex items-center gap-3 text-paper/40 transition-colors duration-150 ease-out hover:text-paper" data-id="element-764" data-mp="27d0fmz5sveoa">
        <ArrowLeftIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:-translate-x-1" strokeWidth={1.25} data-id="element-765" data-mp="1xnql7cnhhjx0" />
        Client Projects
      </button>

      {/* The project itself is the anchor; everything else sits quietly under it. */}
      <header className="mt-8 border-b border-ruleDark pb-9" data-id="element-766" data-mp="2bdbyq7iil6xn">
        <p className="label text-paper/40" data-id="element-767" data-mp="2gd6lpzz65cdf">{project.client}</p>
        <h1 className="mt-5 font-display text-4xl leading-[0.95] tracking-tight sm:text-6xl" data-id="element-768" data-mp="2gbltwi48o7j9">
          {project.name}
        </h1>
        <p className="label mt-6 text-paper/40" data-id="element-769" data-mp="14y1husnax7s8">
          {project.type} · {projectStatus(project)} · {total} deliverable{total === 1 ? '' : 's'} ·
          Last updated {project.updated}
          {project.archived ? ' · Archived' : ''}
        </p>

        {/* Sending the link and checking what it opens are the two things
            staff do most here, so both sit at the top. Preview renders the
            client portal itself, read-only, so it cannot drift out of step. */}
        <ClientLink slug={project.slug} projectId={project.id} data-id="element-770" data-mp="3o3b0bkwktnvx" />
      </header>

      {/* The production work leads the page. */}
      <section aria-labelledby="deliverables-admin" className="mt-14" data-id="element-771" data-mp="2vgzx02hl58vf">
        <div className="flex flex-wrap items-baseline justify-between gap-4 border-b border-ruleDark pb-4" data-id="element-772" data-mp="397uz8vxkwrq1">
          <h2 id="deliverables-admin" className="label m-0 text-paper/40" data-id="element-773" data-mp="34hem7cxo7847">
            Deliverables
          </h2>
          {hidden > 0 && <p className="label-sm m-0 text-paper/35" data-id="element-774" data-mp="2gq95ywntidtd">{hidden} hidden from client</p>}
        </div>

        {total === 0 ? <p className="border-b border-ruleDark py-10 font-display text-xl text-paper/40" data-id="element-775" data-mp="2co5sk2hl8z5g">
            Nothing added yet.
          </p> : <Reorder.Group as="ul" axis="y" values={project.deliverables} onReorder={(next: Deliverable[]) => setDeliverableOrder(next.map(item => item.id))}
      /* A recessed field while a row is lifted, so the empty slot reads as a gap. */ className={['m-0 list-none p-0 transition-colors duration-150 ease-out', carryingId ? 'bg-paper/[0.02]' : ''].join(' ')} data-id="element-776" data-mp="2ufc8cka05jns">
            {project.deliverables.map(item => <DeliverableAdmin key={item.id} deliverable={item} onLift={setCarryingId} data-id="element-777" data-mp="3i1sh9p60eqyh" />)}
          </Reorder.Group>}

        <AddDeliverable data-id="element-778" data-mp="1nqdqd519ilh5" />
      </section>

      <PortalTeam data-id="element-779" data-mp="1nrf6hnx4xy5q" />

      {/* Occasional project-level actions, deliberately quiet. */}
      <section aria-labelledby="project-settings" className="mt-16 border-t border-ruleDark pt-8" data-id="element-780" data-mp="1spqgbynfk8ny">
        <h2 id="project-settings" className="label m-0 text-paper/40" data-id="element-781" data-mp="1l67lmv4tqqzj">
          Project Settings
        </h2>

        <div className="mt-5 flex flex-wrap items-center gap-x-8 gap-y-4" data-id="element-782" data-mp="1gdeiucsx1sq3">
          <button type="button" onClick={() => setPanel(current => current === 'editing' ? 'none' : 'editing')} className={BUTTON_QUIET} data-id="element-783" data-mp="1bgo17l1gqp2k">
            {editing ? 'Close Editor' : 'Edit Project'}
          </button>
          <button type="button" onClick={() => setPanel(current => current === 'template' ? 'none' : 'template')} className={BUTTON_QUIET} data-id="element-784" data-mp="1z5gtn7ivmesf">
            Save as Template
          </button>
          {project.archived ? <button type="button" onClick={() => {
          reopenProject(project.id);
          notify('Project reopened · back in Active Projects');
        }} className={BUTTON_QUIET} data-id="element-785" data-mp="1p9432iimoh1w">
              Reopen Project
            </button> : <button type="button" onClick={() => setPanel('archive')} className={BUTTON_QUIET} data-id="element-786" data-mp="35z4sp29i5swz">
              Archive Project
            </button>}
        </div>

        {confirmArchive && !project.archived && <div className="mt-8 max-w-xl border border-paper/30 px-6 py-6" data-id="element-787" data-mp="2wxngvw34ode3">
            <p className="font-display text-2xl leading-snug" data-id="element-788" data-mp="2ia2ds2696e66">Archive this project?</p>
            <p className="label-sm mt-4 leading-relaxed text-paper/40" data-id="element-789" data-mp="2n0it5rtmw9v6">
              It will be removed from Active Projects but its project history and data will be
              retained.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-6" data-id="element-790" data-mp="2e7z15a7ul3jb">
              <button type="button" onClick={() => {
            archiveProject();
            setPanel('none');
            notify('Project archived · nothing deleted');
            goPortal('projects');
          }} className={BUTTON_SOLID} data-id="element-791" data-mp="2e64glx0hndfr">
                Archive Project
              </button>
              <button type="button" onClick={() => setPanel('none')} className={BUTTON_QUIET} data-id="element-792" data-mp="1gogymkemmb5e">
                Cancel
              </button>
            </div>
          </div>}

        {savingTemplate && <form onSubmit={saveTemplate} className="mt-8 max-w-md" data-id="element-793" data-mp="23lsmujkzgy65">
            <label className="block" data-id="element-794" data-mp="1zm1o9tp7tdr2">
              <span className="label block text-paper/40" data-id="element-795" data-mp="1zl0cfn0dkn8a">Template Name</span>
              <input value={templateName} onChange={event => setTemplateName(event.target.value)} placeholder="e.g. 6-Episode Filmed Podcast" autoFocus className={FIELD} data-id="element-796" data-mp="2dd6jmjljvjvl" />
            </label>
            <div className="mt-6 flex items-center gap-6" data-id="element-797" data-mp="2icjcbkz3rl63">
              <button type="submit" className={BUTTON_SOLID} data-id="element-798" data-mp="2i9wy52hpi7ew">
                Save Template
              </button>
              <button type="button" onClick={() => setPanel('none')} className={BUTTON_QUIET} data-id="element-799" data-mp="17yrxl9srk9qr">
                Cancel
              </button>
            </div>
          </form>}

        {editing && <ProjectFields onClose={() => setPanel('none')} data-id="element-800" data-mp="1hwfw8athq1e7" />}
      </section>

      <ProjectOptions data-id="element-801" data-mp="ka8quypd450s" />
    </div>;
}